/** Shared cloud snapshot helpers: project-id cache, pack wire format, restore chains. */

import { authorizedFetch } from "./cloudAuth";
import {
  readStringPref,
  writeJsonPref,
  type LocalPrefName,
} from "./localPrefs";
import { isJust, isNothing, toMaybe, type Maybe } from "./maybe";
import { trimmedOrEmpty, trimmedOrNull } from "./trimmedString";

/** Keep a thick pack inside snapshot retention (3). */
const THICK_EVERY = 3;

const projectIdMem = new Map<string, string>();
const lastLocalPush = new Map<string, string>();

export type CloudProject = {
  id: string;
  name: string;
  client_project_id?: string | null;
  clientProjectId?: string | null;
};

export type SnapshotMeta = {
  id: string;
  gitHeadOid: string | null;
  gitBaseOid: string | null;
  /** The chronicle's ref oid this snapshot's bundle carries (#105 Phase 1). */
  chronicleHeadOid: string | null;
};

export type DownloadedPack = {
  bundleBase64: string;
  gitHeadOid: string | null;
  gitBaseOid: string | null;
  chronicleHeadOid: string | null;
};

function apiErrorMessage(body: unknown, fallback: string): string {
  if (typeof body === "object" && body !== null && "error" in body) {
    const err = (body as { error?: unknown }).error;
    if (typeof err === "string" && err.length > 0) return err;
  }
  return fallback;
}

function readJsonMap(name: LocalPrefName): Record<string, string> {
  try {
    const rawM = readStringPref(name);
    if (isNothing(rawM)) return {};
    const parsed = JSON.parse(rawM.value) as unknown;
    if (parsed === null || typeof parsed !== "object") return {};
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === "string") {
        const trimmed = trimmedOrNull(v);
        if (trimmed !== null) out[k] = trimmed;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function writeJsonMap(name: LocalPrefName, map: Record<string, string>): void {
  writeJsonPref(name, map);
}

export function cachedCloudProjectId(clientProjectId: string): string | null {
  const hit = projectIdMem.get(clientProjectId);
  if (typeof hit !== "undefined" && hit.length > 0) return hit;
  const id = readJsonMap("cloudApiProjectIds")[clientProjectId];
  if (typeof id !== "undefined" && id.length > 0) {
    projectIdMem.set(clientProjectId, id);
    return id;
  }
  return null;
}

export function rememberCloudProjectId(
  clientProjectId: string,
  cloudId: string,
): void {
  projectIdMem.set(clientProjectId, cloudId);
  const map = readJsonMap("cloudApiProjectIds");
  map[clientProjectId] = cloudId;
  writeJsonMap("cloudApiProjectIds", map);
}

export function forgetCloudProjectId(clientProjectId: string): void {
  projectIdMem.delete(clientProjectId);
  const map = readJsonMap("cloudApiProjectIds");
  delete map[clientProjectId];
  writeJsonMap("cloudApiProjectIds", map);
}

export function noteLocalSnapshotPush(root: string, oid: string): void {
  lastLocalPush.set(root, oid);
}

export function isLocalSnapshotPush(root: string, oid: string | null): boolean {
  return oid !== null && oid.length > 0 && lastLocalPush.get(root) === oid;
}

/**
 * Echo-suppress only manuscript (main) consolidations that match our last
 * upload. Events that only advance the chronicle or a share branch keep
 * the same main tip, so matching `git_head_oid` alone must never skip them
 * (#105 polish) — otherwise other devices miss advances to the chronicle until
 * the next open/manual sync.
 */
export function shouldSuppressRealtimeEcho(
  root: string,
  gitHeadOid: string | null,
  updatedRef: string | null,
): boolean {
  const ref = trimmedOrEmpty(updatedRef);
  if (ref.length > 0 && ref !== "refs/heads/main") return false;
  return isLocalSnapshotPush(root, gitHeadOid);
}

function uploadsSinceThick(clientProjectId: string): number {
  const raw = readJsonMap("cloudUploadsSinceThick")[clientProjectId];
  const n = typeof raw !== "undefined" && raw.length > 0 ? Number(raw) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function setUploadsSinceThick(clientProjectId: string, n: number): void {
  const map = readJsonMap("cloudUploadsSinceThick");
  map[clientProjectId] = String(n);
  writeJsonMap("cloudUploadsSinceThick", map);
}

export function shouldForceThickPack(
  clientProjectId: string,
  lastUploadedOid: string | null,
): boolean {
  if (lastUploadedOid === null || lastUploadedOid.length === 0) return true;
  return uploadsSinceThick(clientProjectId) >= THICK_EVERY - 1;
}

export function recordPackUpload(
  clientProjectId: string,
  thick: boolean,
): void {
  setUploadsSinceThick(
    clientProjectId,
    thick ? 0 : uploadsSinceThick(clientProjectId) + 1,
  );
}

export async function ensureCloudProject(
  clientProjectId: string,
  name: string,
): Promise<string> {
  const cached = cachedCloudProjectId(clientProjectId);
  if (cached !== null) return cached;

  const listRes = await authorizedFetch("/v1/projects");
  if (listRes.ok) {
    const body = (await listRes.json()) as { projects?: CloudProject[] };
    const match = (body.projects ?? []).find((p) => {
      const id = p.client_project_id ?? p.clientProjectId;
      return id === clientProjectId;
    });
    const matchId = match?.id;
    if (typeof matchId !== "undefined" && matchId.length > 0) {
      rememberCloudProjectId(clientProjectId, matchId);
      return matchId;
    }
  }

  const createRes = await authorizedFetch("/v1/projects", {
    method: "POST",
    body: JSON.stringify({
      name,
      client_project_id: clientProjectId,
    }),
  });
  if (createRes.ok === false) {
    const err = await createRes.json().catch(() => ({}));
    throw new Error(
      apiErrorMessage(err, `Create project failed (${createRes.status})`),
    );
  }
  const created = (await createRes.json()) as CloudProject;
  rememberCloudProjectId(clientProjectId, created.id);
  return created.id;
}

function oid(raw: string | null): string | null {
  return trimmedOrNull(raw);
}

function normalizeSnapshot(raw: {
  id: string;
  git_head_oid?: string | null;
  gitHeadOid?: string | null;
  git_base_oid?: string | null;
  gitBaseOid?: string | null;
  chronicle_head_oid?: string | null;
  chronicleHeadOid?: string | null;
}): SnapshotMeta {
  return {
    id: raw.id,
    gitHeadOid: oid(raw.git_head_oid ?? raw.gitHeadOid ?? null),
    gitBaseOid: oid(raw.git_base_oid ?? raw.gitBaseOid ?? null),
    chronicleHeadOid: oid(
      raw.chronicle_head_oid ?? raw.chronicleHeadOid ?? null,
    ),
  };
}

export async function listSnapshots(
  cloudProjectId: string,
): Promise<SnapshotMeta[]> {
  const res = await authorizedFetch(`/v1/projects/${cloudProjectId}/snapshots`);
  if (res.ok === false) {
    throw new Error(`Could not list snapshots (${res.status})`);
  }
  const body = (await res.json()) as {
    snapshots?: Parameters<typeof normalizeSnapshot>[0][];
  };
  return (body.snapshots ?? [])
    .filter((s) => s.id.length > 0)
    .map(normalizeSnapshot);
}

export type CanonicalBackupStatus = "ok" | "empty" | "unreadable";

/** Server-side index check for the project's newest canonical pack. */
export async function fetchCanonicalBackupStatus(
  cloudProjectId: string,
): Promise<CanonicalBackupStatus> {
  const res = await authorizedFetch(
    `/v1/projects/${cloudProjectId}/snapshots/canonical`,
  );
  if (res.ok === false) {
    throw new Error(`Could not verify backup (${res.status})`);
  }
  const body = (await res.json()) as { status?: string };
  if (
    body.status === "ok" ||
    body.status === "empty" ||
    body.status === "unreadable"
  ) {
    return body.status;
  }
  throw new Error(`Unexpected backup status: ${body.status ?? "?"}`);
}

/** Oldest-first packs from the last thick snapshot through `targetId`. */
export function snapshotApplyChain(
  snapshotsNewestFirst: SnapshotMeta[],
  targetId?: string | null,
): SnapshotMeta[] {
  if (snapshotsNewestFirst.length === 0) return [];
  const targetFromId =
    targetId !== null && typeof targetId !== "undefined" && targetId.length > 0
      ? snapshotsNewestFirst.find((s) => s.id === targetId)
      : snapshotsNewestFirst[0];
  const targetMaybe = toMaybe(
    typeof targetFromId === "undefined"
      ? snapshotsNewestFirst[0]
      : targetFromId,
  );
  if (isNothing(targetMaybe)) return [];

  const byHead = new Map<string, SnapshotMeta>();
  for (const s of snapshotsNewestFirst) {
    if (s.gitHeadOid !== null && s.gitHeadOid.length > 0) {
      byHead.set(s.gitHeadOid, s);
    }
  }

  const chain: SnapshotMeta[] = [];
  const seen = new Set<string>();
  let cur: Maybe<SnapshotMeta> = targetMaybe;
  while (isJust(cur) && seen.has(cur.value.id) === false) {
    const snap: SnapshotMeta = cur.value;
    seen.add(snap.id);
    chain.push(snap);
    if (snap.gitBaseOid === null || snap.gitBaseOid.length === 0) break;
    cur = toMaybe(byHead.get(snap.gitBaseOid));
  }
  chain.reverse();
  const firstBase = chain[0]?.gitBaseOid;
  if (
    firstBase !== null &&
    typeof firstBase !== "undefined" &&
    firstBase.length > 0
  ) {
    throw new Error(
      "Cloud backup is missing a full snapshot. Save once more on a device that already has the project.",
    );
  }
  return chain;
}

export function packsNeededAfter(
  chain: SnapshotMeta[],
  localOid: string | null,
): SnapshotMeta[] {
  if (localOid === null || localOid.length === 0) return chain;
  const idx = chain.findIndex((s) => s.gitHeadOid === localOid);
  if (idx >= 0) return chain.slice(idx + 1);
  return chain;
}

function bytesToBase64(bytes: Uint8Array): string {
  const chunks: string[] = [];
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    chunks.push(String.fromCharCode(...bytes.subarray(i, i + chunkSize)));
  }
  return btoa(chunks.join(""));
}

export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export async function uploadSnapshotPack(
  cloudProjectId: string,
  pack: {
    bytes: Uint8Array;
    gitHeadOid: string;
    gitBaseOid?: string | null;
    source?: string;
    /** Which ref this upload advances — omit for `main` (default, unchanged). */
    targetRef?: string;
  },
): Promise<Response> {
  const headers = new Headers();
  headers.set("content-type", "application/octet-stream");
  headers.set("accept", "application/json");
  headers.set("x-git-head-oid", pack.gitHeadOid);
  const gitBase = trimmedOrNull(pack.gitBaseOid);
  if (gitBase !== null) headers.set("x-git-base-oid", gitBase);
  const targetRef = trimmedOrNull(pack.targetRef);
  if (targetRef !== null) headers.set("x-zw-target-ref", targetRef);
  headers.set("x-snapshot-source", pack.source ?? "desktop");

  const res = await authorizedFetch(
    `/v1/projects/${cloudProjectId}/snapshots`,
    {
      method: "POST",
      headers,
      body: pack.bytes as BodyInit,
    },
  );
  if (res.ok) return res;

  if (res.status === 406 || res.status === 415 || res.status === 422) {
    return authorizedFetch(`/v1/projects/${cloudProjectId}/snapshots`, {
      method: "POST",
      body: JSON.stringify({
        bundle_base64: bytesToBase64(pack.bytes),
        git_head_oid: pack.gitHeadOid,
        git_base_oid: pack.gitBaseOid ?? null,
        target_ref: pack.targetRef ?? null,
        source: pack.source ?? "desktop",
      }),
    });
  }

  return res;
}

async function packFromJsonResponse(res: Response): Promise<DownloadedPack> {
  const body = (await res.json()) as {
    bundle_base64?: string;
    bundleBase64?: string;
    git_head_oid?: string | null;
    gitHeadOid?: string | null;
    git_base_oid?: string | null;
    gitBaseOid?: string | null;
    chronicle_head_oid?: string | null;
    chronicleHeadOid?: string | null;
  };
  const bundleBase64 = body.bundle_base64 ?? body.bundleBase64;
  if (typeof bundleBase64 === "undefined" || bundleBase64.length === 0) {
    throw new Error("Restore response missing bundle");
  }
  return {
    bundleBase64: bundleBase64,
    gitHeadOid: oid(body.git_head_oid ?? body.gitHeadOid ?? null),
    gitBaseOid: oid(body.git_base_oid ?? body.gitBaseOid ?? null),
    chronicleHeadOid: oid(
      body.chronicle_head_oid ?? body.chronicleHeadOid ?? null,
    ),
  };
}

export async function downloadSnapshotPack(
  cloudProjectId: string,
  snapshotId: string,
): Promise<DownloadedPack> {
  const path = `/v1/projects/${cloudProjectId}/snapshots/${snapshotId}/restore`;
  const res = await authorizedFetch(path, {
    method: "POST",
    headers: { accept: "application/octet-stream" },
  });

  if (res.ok) {
    const type = trimmedOrEmpty(res.headers.get("content-type")).toLowerCase();
    if (
      type.includes("application/octet-stream") ||
      type.includes("application/x-git")
    ) {
      const buf = new Uint8Array(await res.arrayBuffer());
      if (buf.byteLength === 0)
        throw new Error("Restore response missing pack");
      return {
        bundleBase64: bytesToBase64(buf),
        gitHeadOid: oid(res.headers.get("x-git-head-oid")),
        gitBaseOid: oid(res.headers.get("x-git-base-oid")),
        chronicleHeadOid: oid(res.headers.get("x-zw-chronicle-oid")),
      };
    }
    return packFromJsonResponse(res);
  }

  if (res.status === 406) {
    const jsonRes = await authorizedFetch(path, {
      method: "POST",
      body: "{}",
    });
    if (jsonRes.ok === false) {
      const err = await jsonRes.json().catch(() => ({}));
      throw new Error(
        apiErrorMessage(err, `Restore failed (${jsonRes.status})`),
      );
    }
    return packFromJsonResponse(jsonRes);
  }

  const err = await res.json().catch(() => ({}));
  throw new Error(apiErrorMessage(err, `Restore failed (${res.status})`));
}
